/** Base + registry for typed RPC errors.
 *
 * The backend serializes every handler exception to a payload tagged with its
 * type `code` plus its fields (see api_interface/errors.py). Concrete error
 * classes are codegen'd from the backend RpcError subclasses into
 * `@/app_store/errors` and register themselves here, so `fromPayload` can map
 * an incoming payload to its typed class -- the same way wrapEntity maps a
 * tagged model dump to its generated Store.
 *
 *   import { MergeConflictError } from "@/app_store/errors";
 *   try { await agent.request(...) }
 *   catch (e) {
 *     if (e instanceof MergeConflictError) console.log(e.files); // string[]
 *     else if (e instanceof RpcError) console.log(e.code, e.message);
 *   }
 */
export class RpcError extends Error {
    readonly code: string;
    /** The full wire payload: `code`, `message`, and the error's own fields. */
    readonly detail: Record<string, unknown> | null;

    constructor(code: string, message: string, detail: Record<string, unknown> | null = null) {
        super(message);
        this.name = code;
        this.code = code;
        this.detail = detail;
    }

    static fromPayload(payload: any): RpcError {
        if (payload && typeof payload === "object" && typeof payload.code === "string") {
            const ctor = errorRegistry[payload.code];
            if (ctor) {
                try {
                    return new ctor(payload);
                } catch {
                    // a malformed payload must still surface as a usable error
                }
            }
            return new RpcError(payload.code, payload.message ?? "", payload);
        }
        // disconnect sentinels / legacy string payloads
        return new RpcError("RpcError", typeof payload === "string" ? payload : String(payload));
    }
}

type RpcErrorCtor = new (data: any) => RpcError;

// Populated by every generated `*.generated.ts` error module on import (see
// the registerRpcError call at the bottom of each) -- importing the barrel
// (@/app_store/errors) is enough to fully populate this.
const errorRegistry: Record<string, RpcErrorCtor> = {};

export function registerRpcError(code: string, ctor: RpcErrorCtor): void {
    errorRegistry[code] = ctor;
}
